//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { getHasMultiHandleInstagramEnabled } from '@account-settings/redux/facebook-instagram/selectors/getMultiHandleFeature';
import {
  ContentLibrarySourcesGroups,
  IContentLibrarySourcesOptions,
} from '@apis/content-library/contentLibraryApiModel';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import { DropdownMenu } from '@components/primitives/dropdown-menu/DropdownMenu';
import { MediaLibrarySourcesButton } from '@media-library/components/top-bar/sources-dropdown/MediaLibrarySourcesButton';
import { MediaLibrarySourcesDropdownItem } from '@media-library/components/top-bar/sources-dropdown/MediaLibrarySourcesDropdownItem';
import {
  MediaLibrarySourcesConfigs,
  MediaLibrarySourcesDropdownItemData,
} from '@media-library/components/top-bar/sources-dropdown/mediaLibrarySourcesDropdownConstants';
import {
  computeSourcesSummary,
  mapAllSources,
  mapDropdownStateToSourcesOptions,
} from '@media-library/components/top-bar/sources-dropdown/mediaLibrarySourcesDropdownUtils';
import { AllTeamMembersModal } from '@media-library/components/top-bar/sources-modals/AllTeamMembersModal';
import { AllTrackedHashtagsModal } from '@media-library/components/top-bar/sources-modals/AllTrackedHashtagsModal';
import { AllTrackedUsersModal } from '@media-library/components/top-bar/sources-modals/AllTrackedUsersModal';
import { MediaLibraryTrackingIds } from '@media-library/constants/mediaLibraryTrackingIds';
import { handleChangeSelectParent } from '@media-library/utils/sourcesUtils';
import { getTikTokMentionsFeatureEnabled } from '@publishing/analytics/selectors/getTikTokMentionsFeaturesEnabled';
import { getIsDamImportDropboxEnabled } from '@redux/selectors/getIsDamImportDropboxEnabled';
import { getIsInstagramCompetitorsEnabled } from '@redux/selectors/getIsInstagramCompetitorsEnabled';
import { getUserId } from '@redux/selectors/getSessionInfo';
import { isDefined } from '@utils/filterPredicates';

const { ALL_INSTAGRAM, ALL_TRACKED_HASHTAGS, ALL_TRACKED_USERS, TEAM_MEDIA } =
  ContentLibrarySourcesGroups;

interface MediaLibrarySourcesDropdownProps extends MediaLibrarySourcesConfigs {
  handleChangeSources: (newSource: IContentLibrarySourcesOptions) => void;
  isDisabled?: boolean;
  selectedSources: IContentLibrarySourcesOptions;
}

export const MediaLibrarySourcesDropdown = ({
  handleChangeSources,
  instagramConfigs,
  isDisabled = false,
  pinterestUser,
  selectedSources,
  teamMembers,
  tiktokConfigs,
}: MediaLibrarySourcesDropdownProps) => {
  const isInstagramCompetitorsEnabled = useSelector(
    getIsInstagramCompetitorsEnabled,
  );
  const isDamImportDropboxEnabled = useSelector(getIsDamImportDropboxEnabled);
  const isMultiHandleFeatureEnabled = useSelector(
    getHasMultiHandleInstagramEnabled,
  );

  const isTikTokMentionsEnabled = useSelector(getTikTokMentionsFeatureEnabled);

  const currentUserId = useSelector(getUserId);

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [openedModal, setOpenedModal] = useState<
    ContentLibrarySourcesGroups | undefined
  >(undefined);

  const allSources = mapAllSources({
    currentUserId,
    featureFlags: {
      isInstagramCompetitorsEnabled,
      isDamImportDropboxEnabled,
      isMultiHandleFeatureEnabled,
      isTikTokMentionsEnabled,
    },
    selectedSources,
    sourcesConfigs: {
      instagramConfigs,
      pinterestUser,
      teamMembers,
      tiktokConfigs,
    },
  });

  const sourcesLabel = computeSourcesSummary(allSources);

  const handleChangeSelect = (
    targetSource: MediaLibrarySourcesDropdownItemData,
  ) => {
    const updatedSources = handleChangeSelectParent(allSources, targetSource);
    const currentSourcesOptions =
      mapDropdownStateToSourcesOptions(updatedSources);

    handleChangeSources(currentSourcesOptions);
  };

  const handleChangeFilterText = (newFilterText: string) => {
    setSearchTerm(newFilterText);
  };

  const handleOpenModal = (sourcesGroup: ContentLibrarySourcesGroups) => {
    setIsDropdownOpen(false);
    setOpenedModal(sourcesGroup);
  };

  // Re-render sources modals when their open state changes.
  const getModalKey = (modal: ContentLibrarySourcesGroups) =>
    `${modal}-${openedModal === modal ? 'opened' : 'closed'}`;

  const teamMedia = allSources.subItems.find(
    ({ value }) => value === TEAM_MEDIA,
  );
  const allInstagram = allSources.subItems.find(
    ({ value }) => value === ALL_INSTAGRAM,
  );
  const trackedUsers = allInstagram?.subItems?.find(
    ({ value }) => value === ALL_TRACKED_USERS,
  );
  const trackedHashtags = allInstagram?.subItems?.find(
    ({ value }) => value === ALL_TRACKED_HASHTAGS,
  );

  return (
    <>
      <DropdownMenu
        onOpenChange={setIsDropdownOpen}
        open={!isDisabled && isDropdownOpen}
      >
        <DropdownMenu.Trigger asChild disabled={isDisabled}>
          <MediaLibrarySourcesButton
            label={sourcesLabel}
            onClick={() =>
              setIsDropdownOpen((isDropdownOpen) => !isDropdownOpen)
            }
          />
        </DropdownMenu.Trigger>

        <StyledDropdownContent
          align='start'
          onPointerDownOutside={() => setIsDropdownOpen(false)}
          sideOffset={5}
        >
          <DropdownContentWrapper>
            <SearchBar
              autoFocus={true}
              handleClear={() => handleChangeFilterText('')}
              onChange={handleChangeFilterText}
              placeholder={'Find sources...'}
              trackingIdPrefix={
                MediaLibraryTrackingIds.SOURCES_DROPDOWN_SEARCH_BAR
              }
              value={searchTerm}
              visuallyHiddenLabel={'Search options'}
            />

            <MediaLibrarySourcesDropdownItem
              handleChangeSelect={handleChangeSelect}
              handleOpenModal={handleOpenModal}
              searchTerm={searchTerm}
              source={allSources}
            />
          </DropdownContentWrapper>
        </StyledDropdownContent>
      </DropdownMenu>

      {!isDisabled && isDefined(teamMedia) && (
        <AllTeamMembersModal
          key={getModalKey(TEAM_MEDIA)}
          handleChangeSelects={handleChangeSelect}
          handleCloseModal={() => setOpenedModal(undefined)}
          isOpen={openedModal === TEAM_MEDIA}
          teamMedia={teamMedia}
        />
      )}

      {!isDisabled && isDefined(trackedUsers) && (
        <AllTrackedUsersModal
          key={getModalKey(ALL_TRACKED_USERS)}
          handleChangeSelects={handleChangeSelect}
          handleCloseModal={() => setOpenedModal(undefined)}
          isOpen={openedModal === ALL_TRACKED_USERS}
          trackedUsers={trackedUsers}
        />
      )}

      {!isDisabled && isDefined(trackedHashtags) && (
        <AllTrackedHashtagsModal
          key={getModalKey(ALL_TRACKED_HASHTAGS)}
          handleChangeSelects={handleChangeSelect}
          handleCloseModal={() => setOpenedModal(undefined)}
          isOpen={openedModal === ALL_TRACKED_HASHTAGS}
          trackedHashtags={trackedHashtags}
        />
      )}
    </>
  );
};

const StyledDropdownContent = styled(DropdownMenu.Content)(
  ({ theme }) => `
  height: ${theme.wideMeasure};
  min-width: 300px;
  overflow-y: auto;
  padding: ${theme.spacings[3]};
`,
);

const DropdownContentWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[3]};
`,
);
